import React, { useEffect, useState } from "react"

import { Dropdown, Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Icon from "../../Icon"
import ButtonComponent from "../Button"
import InputNumberComponent from "../Input/InputNumber"
import SelectComponent from "../Select"

import "./DropdownBlock.less"

const { Text } = Typography

const optionsData = [
  {
    value: "is_equal_to",
    label: "is equal to"
  },
  {
    value: "is_between",
    label: "is between"
  },
  {
    value: "is_greater_than",
    label: "is greater than"
  },
  {
    value: "is_less_than",
    label: "is less than"
  }
]

const typeTextMap = {
  is_equal_to: "Equals",
  is_between: "",
  is_greater_than: "Greater than",
  is_less_than: "Less than"
}

const DropdownBlockNumber = ({
  label,
  title,
  iconType,
  id,
  dropdownFilters,
  setDropdownFilters,
  searchPlaceholder,
  min,
  whole_number,
  precision,
  formatValue = (value) => value,
  onOpenChange
}) => {
  const { t } = useTranslation()
  //state toggle show/hide dropdown
  const [visible, setVisible] = useState(false)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isDisabledTransition = useSelector((state) => state.nav.disabledTransition)

  const [selectType, setSelectType] = useState("is_equal_to")
  const [value, setValue] = useState(null)
  const [secondValue, setSecondValue] = useState(null)
  const [isApply, setIsApply] = useState(false)

  const setInitialValue = () => {
    if (dropdownFilters?.type) {
      const { min, max, type } = dropdownFilters
      setSelectType(type)
      setValue(min)
      if (max) setSecondValue(max)
    } else {
      setSelectType("is_equal_to")
      setValue(null)
      setSecondValue(null)
    }
  }

  useEffect(() => {
    setInitialValue()
  }, [dropdownFilters])

  function handleCancel() {
    setInitialValue()
    if (visible) setVisible(false)
  }

  const handleVisibleChange = (value) => {
    if (value) setIsApply(false)
    if (!value && !isApply) handleCancel()
    setVisible(value)
    onOpenChange?.(value)
  }

  const handleChangeType = (value) => {
    setSecondValue(null)
    setSelectType(value)
  }

  const handleApply = () => {
    let newValue =
      value === null
        ? { min: null, max: null, type: null }
        : { min: value, max: secondValue ?? null, type: selectType }
    setIsApply(true)
    setDropdownFilters({ [id]: newValue })
    setVisible(false)
    onOpenChange?.(false)
  }

  function getSelectValue() {
    let result = null

    switch (true) {
      case selectType === "is_between" && (value !== null || secondValue !== null):
        result = `${value !== null ? formatValue(value) : ".."} - ${
          secondValue !== null ? formatValue(secondValue) : ".."
        }`
        break
      case selectType !== "is_between" && value !== null:
        result = `${typeTextMap[selectType]} ${formatValue(value)}`
        break
      default:
        break
    }

    return `${label}${result ? ": " + result : ""}`
  }

  const handleClear = () => {
    setValue(null)
    setSecondValue(null)
  }

  // 0 is a valid value, so every check below compares against null instead of relying on truthiness.
  const hasValue = value !== null
  const hasSecondValue = secondValue !== null
  const isApplyDisabled =
    (!hasValue && hasSecondValue) ||
    (selectType === "is_between" && hasValue && !hasSecondValue) ||
    (selectType === "is_between" && hasValue && hasSecondValue && secondValue <= value)
  const inputPrecision = whole_number ? 0 : precision

  return (
    <div
      className={cls("select-block", {
        "disabled-transition": isDisabledTransition
      })}
    >
      <Dropdown
        overlayClassName={"dropdown-database-wrapper"}
        getPopupContainer={() => document.getElementById("main-layout")}
        placement={isMobile ? "bottom" : "bottomLeft"}
        overlayStyle={{ width: 268 }}
        trigger={["click"]}
        open={visible}
        onOpenChange={handleVisibleChange}
        dropdownRender={() => (
          <Flex
            vertical
            gap={8}
            className="dropdown-database-menu"
            style={{ padding: 12 }}
            data-testid={`filter-dropdown-${id}`}
          >
            <Text strong>{t(title || label)}</Text>
            <SelectComponent
              size={"large"}
              suffixIcon={null}
              options={optionsData}
              value={selectType}
              placeholder={t(searchPlaceholder)}
              onChange={handleChangeType}
            />

            <Flex gap={12} align="center" style={{ paddingLeft: 8 }}>
              <Icon type="arrow_corner_down_right" />

              <InputNumberComponent
                placeholder={0}
                value={value}
                onChange={setValue}
                data-testid={`filter-number-input-${id}`}
                {...(min >= 0 ? { min } : {})}
                step={whole_number ? 1 : 0.01}
                precision={inputPrecision}
                style={{ width: "100%" }}
              />

              {selectType === "is_between" ? (
                <>
                  <Text style={{ textWrap: "nowrap" }}>{t("and")}</Text>
                  <InputNumberComponent
                    placeholder={0}
                    value={secondValue}
                    onChange={setSecondValue}
                    data-testid={`filter-number-input-${id}-to`}
                    step={whole_number ? 1 : 0.01}
                    precision={inputPrecision}
                    style={{ width: "100%" }}
                    {...(min >= 0 ? { min } : {})}
                  />
                </>
              ) : null}
            </Flex>

            <Flex gap={12}>
              {hasValue || hasSecondValue ? (
                <ButtonComponent
                  size="large"
                  onClick={handleClear}
                  data-testid={`filter-clear-button-${id}`}
                >
                  {t("Clear")}
                </ButtonComponent>
              ) : null}

              <ButtonComponent
                size="large"
                type="primary"
                onClick={handleApply}
                block
                data-testid={`filter-apply-button-${id}`}
                disabled={isApplyDisabled}
              >
                {t("Apply")}
              </ButtonComponent>
            </Flex>
          </Flex>
        )}
      >
        <div
          className={cls(
            "dropdown-database",
            {
              "ant-dropdown-active": dropdownFilters?.type
            },
            id
          )}
          data-testid={`filter-pill-${id}`}
        >
          <Icon role="icon" type={iconType} />
          <Text className="dropdown-database_value" data-testid={`filter-pill-value-${id}`}>
            {getSelectValue()}
          </Text>
          <Icon type="chevron_down_small" className="arrow-icon" />
        </div>
      </Dropdown>
    </div>
  )
}

const areEqual = (prev, next) => {
  return JSON.stringify(prev?.dropdownFilters) === JSON.stringify(next?.dropdownFilters)
}

export default React.memo(DropdownBlockNumber, areEqual)
